/**
 * Module access is a flat list of menus in the database, but the sidebar shows
 * some of them as a group with sub-items (Staff → Recruitment, Staff Compliance,
 * Leave & Approval). This file is the one place that says which menu is a child
 * of which, so the access pickers can present the same shape as the nav.
 *
 * Nothing here changes what the API stores — menu_ids stays a flat array of ids.
 */

// A menu can exist under more than one spelling, hence a list of codes per node.
export const MENU_GROUPS = [
    {
        parent: ['staffs', 'staff'],
        children: [
            // Granted along with the parent when the parent is ticked.
            { codes: ['recruitment'], autoWithParent: true, managerOnlyGrant: true },
            { codes: ['staff_compliance'], autoWithParent: true, managerOnlyGrant: true },
            // Deliberately NOT auto-granted: approving other people's leave is a
            // separate decision from being given the Staff module. Unlike the
            // others it has always been an admin-assignable menu, so it stays in
            // the admin's picker as well.
            { codes: ['leave_approval', 'leave_approvals'], autoWithParent: false },
            // Not auto-granted either — it exposes everyone's pay, so it is
            // ticked on purpose, never as a side effect of granting Staff.
            { codes: ['payroll'], autoWithParent: false, managerOnlyGrant: true },
            // Agencies (care homes only) — booking cover from staffing
            // agencies commits the home to paying for it, so it is ticked on
            // purpose like Payroll. Managers and owners hold it by role.
            { codes: ['agencies'], autoWithParent: false, managerOnlyGrant: true },
        ],
    },
    {
        parent: ['staffs_reports', 'staffs_report'],
        children: [
            // Maintenance reports — for the maintenance team: only the reports
            // assigned to Maintenance (Maintenance & repairs, Safety & security
            // with maintenance needed, or handed over —
            // api/utils/staffReportService.js), on the dashboard
            // AND in the app. Ticked on its own for someone who should NOT see
            // every report; the full Staffs Report module already includes it.
            { codes: ['staff_reports_maintenance'], autoWithParent: false, managerOnlyGrant: true },
        ],
    },
    {
        parent: ['residents'],
        children: [
            // The org-wide care-notes feed. Comes with Residents by default —
            // it only shows notes the holder could already open resident by
            // resident — but can be unticked on its own. Admin-grantable, so
            // it appears in the admin's organisation menu picker too.
            { codes: ['care_notes'], autoWithParent: true },
            // Enquiries & Assessments — the step before the Enquiry List.
            // Comes with Residents by default (whoever manages residents takes
            // the enquiries), and can be unticked on its own.
            { codes: ['enquiries'], autoWithParent: true },
        ],
    },
];

const matches = (menu, codes) => !!menu && codes.includes(menu.code);

/**
 * Sub-menus that only a MANAGER hands out, when they create or edit a staff
 * member. At admin level an organisation is given the whole Staff module and
 * nothing finer — the manager then decides which of its screens each of their
 * staff can reach.
 */
const managerOnlyCodes = MENU_GROUPS.flatMap((g) =>
    g.children.filter((c) => c.managerOnlyGrant).flatMap((c) => c.codes),
);

/** Is this a sub-menu that only a manager grants (never an admin)? */
export const isManagerOnlyMenu = (menu) => !!menu && managerOnlyCodes.includes(menu.code);

/** Drop the manager-only sub-menus from a list — for the admin's menu picker. */
export const withoutManagerOnlyMenus = (menus = []) => menus.filter((m) => !isManagerOnlyMenu(m));

/**
 * Modules that only exist for a care home. They are offered in the access
 * pickers of a care-home organisation only: granted to a domiciliary user
 * they would open nothing (the sidebar hides them and the API refuses them).
 */
export const CARE_HOME_ONLY_MENU_CODES = ['care_planner', 'agencies'];

/** Drop the care-home-only modules from a list when the organisation is domiciliary. */
export const withoutCareHomeOnlyMenus = (menus = [], organizationType) =>
    (String(organizationType || '').toLowerCase() === 'domiciliary'
        ? menus.filter((m) => !CARE_HOME_ONLY_MENU_CODES.includes(m.code))
        : menus);

const groupFor = (menu) => MENU_GROUPS.find((g) => matches(menu, g.parent));

const childNodeFor = (menu) =>
    MENU_GROUPS.reduce((found, g) => found || g.children.find((c) => matches(menu, c.codes)), null);

/** Is this menu a sub-item of another menu? */
export const isChildMenu = (menu) => !!childNodeFor(menu);

/**
 * Order a flat menu list so each parent is immediately followed by its children,
 * and tag every entry with its depth (0 = module, 1 = sub-menu). A child whose
 * parent menu isn't in the list is left at depth 0 — on its own it is simply a
 * module of its own.
 */
export const orderMenusForPicker = (menus = []) => {
    const remaining = [...menus];
    const take = (predicate) => {
        const index = remaining.findIndex(predicate);
        return index === -1 ? null : remaining.splice(index, 1)[0];
    };

    const ordered = [];
    while (remaining.length > 0) {
        const menu = remaining.shift();
        const group = groupFor(menu);
        ordered.push({ ...menu, depth: 0 });
        if (!group) continue;
        group.children.forEach((child) => {
            const found = take((m) => matches(m, child.codes));
            if (found) ordered.push({ ...found, depth: 1 });
        });
    }
    return ordered;
};

/**
 * Tick or untick a menu, keeping the group sensible:
 *   • ticking a parent also ticks the children marked autoWithParent
 *   • unticking a parent unticks every one of its children
 *   • a child on its own is just itself
 *
 * @param {number[]} menuIds   currently selected ids
 * @param {object[]} allMenus  every assignable menu (to resolve child ids)
 * @param {object}   menu      the menu being toggled
 * @returns {number[]} the new selection
 */
export const toggleMenuWithFamily = (menuIds = [], allMenus = [], menu) => {
    const current = new Set(menuIds);
    const selecting = !current.has(menu.id);
    const group = groupFor(menu);

    const idsFor = (codes) => allMenus.filter((m) => codes.includes(m.code)).map((m) => m.id);

    if (group) {
        if (selecting) {
            current.add(menu.id);
            group.children
                .filter((c) => c.autoWithParent)
                .forEach((c) => idsFor(c.codes).forEach((id) => current.add(id)));
        } else {
            current.delete(menu.id);
            group.children.forEach((c) => idsFor(c.codes).forEach((id) => current.delete(id)));
        }
        return Array.from(current);
    }

    if (selecting) current.add(menu.id);
    else current.delete(menu.id);
    return Array.from(current);
};
